import { callNetlifyFunction } from "./apiBase";

// ---------- helpers ----------
function ensureTrailingSlash(p) {
  if (!p) return "";
  const s = String(p).trim();
  if (!s) return "";
  return s.endsWith("/") ? s : `${s}/`;
}

/**
 * Build a query string safely without double-encoding slashes.
 * Using URLSearchParams ensures proper encoding and avoids turning "/" into "%2F"
 * in a way that can confuse server-side query parsing in some environments.
 */
function withQuery(path, params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

// ---------- Firmware endpoints ----------

export function getFirmwareFolders() {
  return callNetlifyFunction("firmware-folders", { method: "GET" });
}

export function browseFirmware(prefix) {
  const p = ensureTrailingSlash(prefix);
  if (!p) throw new Error("Chybí prefix.");

  // Important: do NOT encodeURIComponent() the whole prefix.
  // Let URLSearchParams handle encoding to avoid double-encoding issues.
  const url = withQuery("firmware-browse", { prefix: p });
  return callNetlifyFunction(url, { method: "GET" });
}

/**
 * Create a folder in Firestore catalog
 * Body: { prefix: "HCC07-IP/RT-07_IP/release/", displayName?: "release" }
 */
export function createFirmwareFolder(payload) {
  if (!payload || !payload.prefix) throw new Error("Chybí payload.prefix.");
  return callNetlifyFunction("firmware-folder-create", {
    method: "POST",
    body: { ...payload, prefix: ensureTrailingSlash(payload.prefix) },
  });
}

/**
 * Rename a folder in Firestore catalog (displayName only)
 * Body: { path: "HCC07-IP/RT-07_IP/release/", newDisplayName: "Release" }
 */
export function renameFirmwareFolder(payload) {
  if (!payload || !payload.path) throw new Error("Chybí payload.path.");
  if (!payload.newDisplayName) throw new Error("Chybí payload.newDisplayName.");
  return callNetlifyFunction("firmware-folder-rename", {
    method: "POST",
    body: { ...payload, path: ensureTrailingSlash(payload.path) },
  });
}

/**
 * REAL folder rename/move (R2 prefix move + update catalog paths)
 * Body: { fromPrefix: "HCC07-IP/to_delete3/", toPrefix: "HCC07-IP/to_delete4/" }
 */
export function moveFirmwareFolder(payload) {
  if (!payload || !payload.fromPrefix)
    throw new Error("Chybí payload.fromPrefix.");
  if (!payload.toPrefix) throw new Error("Chybí payload.toPrefix.");

  return callNetlifyFunction("firmware-folder-move", {
    method: "POST",
    body: {
      ...payload,
      fromPrefix: ensureTrailingSlash(payload.fromPrefix),
      toPrefix: ensureTrailingSlash(payload.toPrefix),
    },
  });
}

/**
 * Soft-delete folder (tombstone in Firestore), only if empty in R2
 * Body: { path: "HCC07-IP/RT-07_IP/release/" }
 */
export function deleteFirmwareFolder(payload) {
  if (!payload || !payload.path) throw new Error("Chybí payload.path.");
  return callNetlifyFunction("firmware-folder-delete", {
    method: "POST",
    body: { ...payload, path: ensureTrailingSlash(payload.path) },
  });
}

/**
 * Delete file from R2 by key (requires canManageFirmware)
 * Body: { key: "HCC07-IP/..." }
 */
export function deleteFirmwareFile(payload) {
  if (!payload || !payload.key) throw new Error("Chybí payload.key.");
  return callNetlifyFunction("firmware-delete", {
    method: "POST",
    body: payload,
  });
}

/**
 * Delete WHOLE device/system (root) including all R2 objects + Firestore cleanup
 * Body: { device: "HCC-TEST" }
 */
export function deleteFirmwareDevice(payload) {
  if (!payload || !payload.device) throw new Error("Chybí payload.device.");
  return callNetlifyFunction("firmware-device-delete", {
    method: "POST",
    body: payload,
  });
}
